import { NewsletterSection } from '@/components/home/NewsletterSection';
import { InfographiesGrid } from '@/components/infographies/InfographiesGrid';
import { useInfographies } from '@/hooks/useInfographies';
import {
  Image,
  Users,
  Presentation,
  Mail,
  Phone,
  CheckCircle,
  Palette,
  FileImage,
  Loader2,
  Target,
  BarChart3,
  FileText,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SEOHead } from '@/components/SEO/SEOHead';

export function InfographiesPage() {
  const { t, i18n } = useTranslation('infographies');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  const { infographies, loading, error } = useInfographies();

  // Filtrer les infographies à exclure
  const filteredInfographies = infographies.filter(
    (infographie) =>
      !['Agritech BPI France', 'Agritech XAnge', 'Agritech Citizen Capital'].includes(
        infographie.title
      )
  );

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#FF6B6B] to-[#e05555] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Breadcrumb */}
            <div className="text-sm mb-6 opacity-90">
              <a href={`/${currentLocale}`} className="hover:underline">
                {t('breadcrumb.home')}
              </a>
              {' > '}
              <a href={`/${currentLocale}/prestations`} className="hover:underline">
                {t('breadcrumb.prestations')}
              </a>
              {' > '}
              <span>{t('breadcrumb.current')}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Texte */}
              <div>
                <h1 className="text-4xl md:text-6xl font-heading font-extrabold mb-6">
                  {t('hero.title')}
                </h1>
                <p className="text-xl mb-8 opacity-95">
                  {t('hero.subtitle')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`/${currentLocale}/contact`}
                    className="px-8 py-4 bg-white text-[#FF6B6B] rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg text-center"
                  >
                    {t('hero.ctaPrimary')}
                  </a>
                  <a
                    href="#annuaire"
                    className="px-8 py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center"
                  >
                    {t('hero.ctaSecondary')}
                  </a>
                </div>
              </div>

              {/* Visuel */}
              <div className="bg-white/10 rounded-lg p-8 backdrop-blur-sm border border-white/20">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded p-4 text-center">
                    <FileImage className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Formats A4/A3</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Palette className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Votre charte</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Image className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Print & Web</p>
                  </div>
                  <div className="bg-white/10 rounded p-4 text-center">
                    <Presentation className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Sources cités</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* En bref */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
              Des infographies sur-mesure pour l'agri-tech
            </h2>
            <p className="text-center text-aspexit-gray mb-16 max-w-2xl mx-auto">
              Visuels pédagogiques qui synthétisent les sujets complexes du numérique agricole
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 1: Vue 360° */}
              <div className="bg-gradient-to-br from-[#FF6B6B]/5 to-white rounded-2xl p-8 border-2 border-[#FF6B6B]/20 hover:border-[#FF6B6B]/40 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Cercles concentriques représentant une vue 360° */}
                    <circle cx="50" cy="50" r="40" fill="none" stroke="#FF6B6B" strokeWidth="2" opacity="0.2"/>
                    <circle cx="50" cy="50" r="28" fill="none" stroke="#FF6B6B" strokeWidth="2" opacity="0.4"/>
                    <circle cx="50" cy="50" r="16" fill="none" stroke="#FF6B6B" strokeWidth="3" opacity="0.6"/>
                    <circle cx="50" cy="50" r="6" fill="#FF6B6B"/>
                    {/* Rayons */}
                    <line x1="50" y1="10" x2="50" y2="25" stroke="#FF6B6B" strokeWidth="2" opacity="0.5"/>
                    <line x1="50" y1="75" x2="50" y2="90" stroke="#FF6B6B" strokeWidth="2" opacity="0.5"/>
                    <line x1="10" y1="50" x2="25" y2="50" stroke="#FF6B6B" strokeWidth="2" opacity="0.5"/>
                    <line x1="75" y1="50" x2="90" y2="50" stroke="#FF6B6B" strokeWidth="2" opacity="0.5"/>
                    {/* Texte 360 */}
                    <text x="50" y="58" fontSize="14" fontWeight="bold" fill="#FF6B6B" textAnchor="middle" fontFamily="system-ui">360°</text>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-3 text-center">
                  Vue à 360° de la thématique
                </h3>
                <p className="text-aspexit-gray text-center leading-relaxed">
                  Chaque infographie offre une vision complète et structurée d'un sujet agri-tech, intégrant acteurs, technologies et enjeux
                </p>
              </div>

              {/* Card 2: Collaboration client */}
              <div className="bg-gradient-to-br from-[#FF6B6B]/5 to-white rounded-2xl p-8 border-2 border-[#FF6B6B]/20 hover:border-[#FF6B6B]/40 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Deux personnes qui collaborent */}
                    <circle cx="35" cy="30" r="12" fill="#FF6B6B" opacity="0.8"/>
                    <path d="M20 65 Q20 48 35 48 Q50 48 50 65 Z" fill="#FF6B6B" opacity="0.8"/>
                    <circle cx="65" cy="30" r="12" fill="#FF6B6B"/>
                    <path d="M50 65 Q50 48 65 48 Q80 48 80 65 Z" fill="#FF6B6B"/>
                    {/* Flèches bidirectionnelles */}
                    <path d="M42 40 L58 40" stroke="#FF6B6B" strokeWidth="3" fill="none"/>
                    <path d="M58 40 L54 36 M58 40 L54 44" stroke="#FF6B6B" strokeWidth="2" fill="none" strokeLinecap="round"/>
                    <path d="M42 40 L46 36 M42 40 L46 44" stroke="#FF6B6B" strokeWidth="2" fill="none" strokeLinecap="round"/>
                    {/* Étoiles de créativité */}
                    <circle cx="30" cy="20" r="2" fill="#FF6B6B" opacity="0.6"/>
                    <circle cx="70" cy="18" r="2.5" fill="#FF6B6B" opacity="0.6"/>
                    <circle cx="50" cy="70" r="2" fill="#FF6B6B" opacity="0.6"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-3 text-center">
                  Format travaillé avec vous
                </h3>
                <p className="text-aspexit-gray text-center leading-relaxed">
                  Design personnalisé selon votre charte graphique et vos besoins : A4, poster, présentation ou web
                </p>
              </div>

              {/* Card 3: Thématiques larges */}
              <div className="bg-gradient-to-br from-[#FF6B6B]/5 to-white rounded-2xl p-8 border-2 border-[#FF6B6B]/20 hover:border-[#FF6B6B]/40 hover:shadow-xl transition-all duration-300">
                <div className="w-20 h-20 mx-auto mb-6">
                  <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                    {/* Plante (agriculture) */}
                    <path d="M30 70 L30 45 M30 45 C30 40 35 35 40 33 M30 45 C30 40 25 35 20 33" stroke="#55BC8C" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    <circle cx="35" cy="50" r="3" fill="#55BC8C" opacity="0.7"/>
                    <circle cx="25" cy="55" r="2.5" fill="#55BC8C" opacity="0.7"/>
                    <ellipse cx="30" cy="73" rx="8" ry="3" fill="#55BC8C" opacity="0.4"/>

                    {/* Circuit numérique */}
                    <circle cx="70" cy="30" r="6" fill="#FF6B6B"/>
                    <circle cx="60" cy="50" r="5" fill="#FF6B6B" opacity="0.7"/>
                    <circle cx="80" cy="55" r="5" fill="#FF6B6B" opacity="0.7"/>
                    <circle cx="70" cy="70" r="6" fill="#FF6B6B"/>
                    {/* Connections */}
                    <line x1="70" y1="36" x2="62" y2="46" stroke="#FF6B6B" strokeWidth="2"/>
                    <line x1="70" y1="36" x2="78" y2="51" stroke="#FF6B6B" strokeWidth="2"/>
                    <line x1="62" y1="54" x2="70" y2="64" stroke="#FF6B6B" strokeWidth="2"/>
                    <line x1="78" y1="59" x2="70" y2="64" stroke="#FF6B6B" strokeWidth="2"/>

                    {/* Lien entre agriculture et numérique */}
                    <path d="M45 60 Q52 55 58 52" stroke="#6B7280" strokeWidth="2" strokeDasharray="4,3" fill="none"/>
                  </svg>
                </div>
                <h3 className="text-xl font-heading font-bold text-aspexit-black mb-3 text-center">
                  Thématiques variées
                </h3>
                <p className="text-aspexit-gray text-center leading-relaxed">
                  Du capteur IoT à l'IA, de la cartographie au biocontrôle : toutes les facettes du numérique agricole
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Annuaire des infographies */}
      <section id="annuaire" className="py-24 bg-[#FF6B6B]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-6 text-center">
              Mes infographies
            </h2>
            <p className="text-center text-aspexit-gray mb-12 max-w-2xl mx-auto">
              Découvrez les infographies que j'ai réalisées, classées par thématique
            </p>

            {loading && (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
                <span className="ml-3 text-aspexit-gray">Chargement des infographies...</span>
              </div>
            )}

            {error && (
              <div className="text-center py-12">
                <p className="text-red-500">Une erreur est survenue lors du chargement des infographies.</p>
              </div>
            )}

            {!loading && !error && filteredInfographies.length === 0 && (
              <div className="text-center py-12">
                <p className="text-aspexit-gray">Aucune infographie disponible pour le moment.</p>
              </div>
            )}

            {!loading && !error && filteredInfographies.length > 0 && (
              <InfographiesGrid infographies={filteredInfographies} groupByCategory={false} />
            )}
          </div>
        </div>
      </section>

      {/* Pourquoi moi */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-12 text-center">
              Pourquoi travailler avec moi
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#FF6B6B] mb-2">
                  50+
                </div>
                <p className="text-aspexit-gray">Infographies réalisées</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#FF6B6B] mb-2">
                  10+
                </div>
                <p className="text-aspexit-gray">
                  Années de vulgarisation scientifique
                </p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#FF6B6B] mb-2">
                  100%
                </div>
                <p className="text-aspexit-gray">Pédagogie et clarté</p>
              </div>
            </div>

            <div className="bg-aspexit-gray-light rounded-lg p-8">
              <h3 className="text-xl font-heading font-semibold text-aspexit-black mb-4">
                Mes atouts
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-aspexit-gray">
                <li className="flex items-center gap-2">
                  <span className="text-[#FF6B6B]">✓</span>
                  Double compétence : contenu + design
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF6B6B]">✓</span>
                  Expertise technique agricole
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF6B6B]">✓</span>
                  Vulgarisation pédagogique
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF6B6B]">✓</span>
                  Respect de votre charte graphique
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF6B6B]">✓</span>
                  Sources scientifiques vérifiées
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#FF6B6B]">✓</span>
                  Formats adaptés à vos besoins
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 bg-gradient-to-br from-[#FF6B6B] to-[#e05555] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-heading font-bold mb-4">
              Besoin d'une infographie ?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Décrivez votre projet pour recevoir un devis personnalisé
            </p>

            <div className="flex justify-center">
              <a
                href="/contact"
                className="px-8 py-4 bg-white text-[#FF6B6B] rounded-lg font-semibold hover:bg-aspexit-gray-light transition-colors shadow-lg inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-5 h-5" />
                Commander une infographie
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />
    </div>
  );
}
